'use client'

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faArrowLeft,
    faBookOpen,
    faSearch,
    faTimes,
    faLayerGroup,
    faBook,
    faChevronLeft,
    faChevronRight,
    faNewspaper,
    faStar,
    faHeart,
    faFire,
    faFeather,
    faBookmark,
    faFeatherPointed,
    faGlobe
} from "@fortawesome/free-solid-svg-icons"
import { useGlobalContext } from "../component/context/provider"
import { AxiosGetData } from "../component/context/axiosfunction"
import CardEbook from "../component/Card/Card"
import { Pagination } from "antd"

// กำหนดธีมสี ไอคอน และแถบสีสำหรับแต่ละประเภทหนังสือ (ขึ้นกับธีมที่ผู้ใช้เลือกในระบบ isDark ไม่ขึ้นกับ browser)
const TYPE_THEMES = {
    all: {
        icon: faLayerGroup,
        active: "bg-gradient-to-r from-[#EC008C] to-[#d6007e] text-white border-[#EC008C] shadow-md shadow-[#EC008C]/25",
        inactiveLight: "bg-white text-gray-700 border-gray-200 hover:border-[#EC008C] hover:text-[#EC008C]",
        inactiveDark: "bg-[#1e1e1e] text-zinc-200 border-zinc-800 hover:border-[#EC008C] hover:text-[#EC008C]",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-pink-50 text-[#EC008C]",
        badgeInactiveDark: "bg-pink-950/60 text-pink-300",
        bar: "bg-[#EC008C]",
        iconColor: "text-[#EC008C]",
    },
    "นิตยสาร": {
        icon: faNewspaper,
        active: "bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white border-fuchsia-600 shadow-md shadow-fuchsia-500/25",
        inactiveLight: "bg-fuchsia-50/70 text-fuchsia-700 border-fuchsia-200/80 hover:bg-fuchsia-100",
        inactiveDark: "bg-fuchsia-950/40 text-fuchsia-300 border-fuchsia-900/60 hover:bg-fuchsia-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-fuchsia-100 text-fuchsia-800",
        badgeInactiveDark: "bg-fuchsia-900/60 text-fuchsia-200",
        bar: "bg-fuchsia-500",
        iconColor: "text-fuchsia-500",
    },
    "วรรณกรรมเยาวชน": {
        icon: faStar,
        active: "bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-emerald-600 shadow-md shadow-emerald-500/25",
        inactiveLight: "bg-emerald-50/70 text-emerald-700 border-emerald-200/80 hover:bg-emerald-100",
        inactiveDark: "bg-emerald-950/40 text-emerald-300 border-emerald-900/60 hover:bg-emerald-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-emerald-100 text-emerald-800",
        badgeInactiveDark: "bg-emerald-900/60 text-emerald-200",
        bar: "bg-emerald-500",
        iconColor: "text-emerald-500",
    },
    "นิยาย": {
        icon: faHeart,
        active: "bg-gradient-to-r from-rose-500 to-pink-600 text-white border-rose-500 shadow-md shadow-rose-500/25",
        inactiveLight: "bg-rose-50/70 text-rose-700 border-rose-200/80 hover:bg-rose-100",
        inactiveDark: "bg-rose-950/40 text-rose-300 border-rose-900/60 hover:bg-rose-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-rose-100 text-rose-800",
        badgeInactiveDark: "bg-rose-900/60 text-rose-200",
        bar: "bg-rose-500",
        iconColor: "text-rose-500",
    },
    "การ์ตูน": {
        icon: faFire,
        active: "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-amber-500 shadow-md shadow-amber-500/25",
        inactiveLight: "bg-amber-50/70 text-amber-800 border-amber-200/80 hover:bg-amber-100",
        inactiveDark: "bg-amber-950/40 text-amber-300 border-amber-900/60 hover:bg-amber-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-amber-100 text-amber-900",
        badgeInactiveDark: "bg-amber-900/60 text-amber-200",
        bar: "bg-amber-500",
        iconColor: "text-amber-500",
    },
    "อีบุ๊คทั่วไป": {
        icon: faBook,
        active: "bg-gradient-to-r from-sky-500 to-blue-600 text-white border-sky-500 shadow-md shadow-sky-500/25",
        inactiveLight: "bg-sky-50/70 text-sky-700 border-sky-200/80 hover:bg-sky-100",
        inactiveDark: "bg-sky-950/40 text-sky-300 border-sky-900/60 hover:bg-sky-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-sky-100 text-sky-800",
        badgeInactiveDark: "bg-sky-900/60 text-sky-200",
        bar: "bg-sky-500",
        iconColor: "text-sky-500",
    },
    "นวนิยาย": {
        icon: faFeather,
        active: "bg-gradient-to-r from-violet-600 to-purple-600 text-white border-violet-600 shadow-md shadow-violet-500/25",
        inactiveLight: "bg-violet-50/70 text-violet-700 border-violet-200/80 hover:bg-violet-100",
        inactiveDark: "bg-violet-950/40 text-violet-300 border-violet-900/60 hover:bg-violet-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-violet-100 text-violet-800",
        badgeInactiveDark: "bg-violet-900/60 text-violet-200",
        bar: "bg-violet-500",
        iconColor: "text-violet-500",
    },
    "รวมเรื่องสั้น": {
        icon: faBookmark,
        active: "bg-gradient-to-r from-teal-500 to-emerald-600 text-white border-teal-500 shadow-md shadow-teal-500/25",
        inactiveLight: "bg-teal-50/70 text-teal-700 border-teal-200/80 hover:bg-teal-100",
        inactiveDark: "bg-teal-950/40 text-teal-300 border-teal-900/60 hover:bg-teal-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-teal-100 text-teal-800",
        badgeInactiveDark: "bg-teal-900/60 text-teal-200",
        bar: "bg-teal-500",
        iconColor: "text-teal-500",
    },
    "วรรณกรรมไทย": {
        icon: faFeatherPointed,
        active: "bg-gradient-to-r from-orange-500 to-red-500 text-white border-orange-500 shadow-md shadow-orange-500/25",
        inactiveLight: "bg-orange-50/70 text-orange-800 border-orange-200/80 hover:bg-orange-100",
        inactiveDark: "bg-orange-950/40 text-orange-300 border-orange-900/60 hover:bg-orange-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-orange-100 text-orange-800",
        badgeInactiveDark: "bg-orange-900/60 text-orange-200",
        bar: "bg-orange-500",
        iconColor: "text-orange-500",
    },
    "วรรณกรรมแปล": {
        icon: faGlobe,
        active: "bg-gradient-to-r from-indigo-500 to-blue-600 text-white border-indigo-500 shadow-md shadow-indigo-500/25",
        inactiveLight: "bg-indigo-50/70 text-indigo-700 border-indigo-200/80 hover:bg-indigo-100",
        inactiveDark: "bg-indigo-950/40 text-indigo-300 border-indigo-900/60 hover:bg-indigo-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-indigo-100 text-indigo-800",
        badgeInactiveDark: "bg-indigo-900/60 text-indigo-200",
        bar: "bg-indigo-500",
        iconColor: "text-indigo-500",
    },
}

const FALLBACK_THEMES = [
    {
        icon: faBook,
        active: "bg-gradient-to-r from-cyan-600 to-blue-600 text-white border-cyan-600 shadow-md shadow-cyan-500/25",
        inactiveLight: "bg-cyan-50/70 text-cyan-700 border-cyan-200/80 hover:bg-cyan-100",
        inactiveDark: "bg-cyan-950/40 text-cyan-300 border-cyan-900/60 hover:bg-cyan-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-cyan-100 text-cyan-800",
        badgeInactiveDark: "bg-cyan-900/60 text-cyan-200",
        bar: "bg-cyan-500",
        iconColor: "text-cyan-500",
    },
    {
        icon: faBookOpen,
        active: "bg-gradient-to-r from-lime-600 to-emerald-600 text-white border-lime-600 shadow-md shadow-lime-500/25",
        inactiveLight: "bg-lime-50/70 text-lime-700 border-lime-200/80 hover:bg-lime-100",
        inactiveDark: "bg-lime-950/40 text-lime-300 border-lime-900/60 hover:bg-lime-900/50",
        badgeActive: "bg-white/25 text-white",
        badgeInactiveLight: "bg-lime-100 text-lime-800",
        badgeInactiveDark: "bg-lime-900/60 text-lime-200",
        bar: "bg-lime-500",
        iconColor: "text-lime-500",
    }
]

const getTypeTheme = (typeName, isDark, index = 0) => {
    const rawTheme = (typeName && TYPE_THEMES[typeName]) ? TYPE_THEMES[typeName] : FALLBACK_THEMES[index % FALLBACK_THEMES.length]
    return {
        icon: rawTheme.icon,
        active: rawTheme.active,
        inactive: isDark ? rawTheme.inactiveDark : rawTheme.inactiveLight,
        badgeActive: rawTheme.badgeActive,
        badgeInactive: isDark ? rawTheme.badgeInactiveDark : rawTheme.badgeInactiveLight,
        bar: rawTheme.bar,
        iconColor: rawTheme.iconColor,
    }
}

const Cate = ({ cateId }) => {
    const router = useRouter()
    const { API, headers, privateKey, theme } = useGlobalContext()
    const isDark = theme === 'dark_theme'

    // State จัดการหมวดหมู่ที่เลือก (ถ้าไม่มีค่าจะแสดงหน้าหมวดหมู่ทั้งหมด)
    const [selectedId, setSelectedId] = useState(cateId || null)
    const [currentCategory, setCurrentCategory] = useState(null)
    const [categories, setCategories] = useState([])
    const [typeCategories, setTypeCategories] = useState([])
    const [selectedTypeId, setSelectedTypeId] = useState('all') // 'all' หรือ id ของ BookType
    const [categorySearch, setCategorySearch] = useState("")

    // State รายการหนังสือและการโหลด
    const [books, setBooks] = useState([])
    const [totalBooks, setTotalBooks] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [errorMsg, setErrorMsg] = useState(null)

    const activeRequestId = useRef(0)
    const bookGridRef = useRef(null)

    // กรองเฉพาะหมวดหมู่ที่มีหนังสืออย่างน้อย 1 เล่ม (ถ้าหมวดหมู่ไม่มีหนังสือไม่ต้องโชว์)
    const activeCategories = useMemo(() => {
        return (categories || []).filter(cat => cat.book_count == null || Number(cat.book_count) > 0)
    }, [categories])

    // กรองหมวดหมู่ตามข้อความค้นหา (สำหรับหน้าหมวดหมู่ทั้งหมด)
    const filteredCategories = useMemo(() => {
        const base = activeCategories
        if (!categorySearch.trim()) return base
        const search = categorySearch.toLowerCase().trim()
        return base.filter(cat => cat.name?.toLowerCase().includes(search))
    }, [activeCategories, categorySearch])

    // กรองกลุ่มประเภทหนังสือเฉพาะที่มีหมวดหมู่ที่มีหนังสือ
    const activeTypeCategories = useMemo(() => {
        if (!typeCategories || typeCategories.length === 0) return []
        return typeCategories
            .map(group => {
                const validCategories = (group.categories || []).filter(c => c.book_count == null || Number(c.book_count) > 0)
                return validCategories.length > 0 ? { ...group, categories: validCategories } : null
            })
            .filter(Boolean)
    }, [typeCategories])

    // รายการประเภทหนังสือสำหรับแสดงแบ่งกลุ่ม
    const displayedTypeGroups = useMemo(() => {
        if (activeTypeCategories.length === 0) return []

        // ถ้ามีการค้นหาข้อความ
        if (categorySearch.trim()) {
            const search = categorySearch.toLowerCase().trim()
            return activeTypeCategories
                .map(group => {
                    const matchedCates = group.categories.filter(c => c.name?.toLowerCase().includes(search))
                    return matchedCates.length > 0 ? { ...group, categories: matchedCates } : null
                })
                .filter(Boolean)
        }

        // ถ้าเลือกแท็บประเภทเฉพาะเจาะจง
        if (selectedTypeId !== 'all') {
            return activeTypeCategories.filter(group => String(group.type_id) === String(selectedTypeId))
        }

        return activeTypeCategories
    }, [activeTypeCategories, categorySearch, selectedTypeId])

    // ควบคุมการคลิกเลื่อนแถบแท็บหมวดหมู่หลัก (Click-to-Slide Tabs)
    const tabScrollRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkTabScroll = useCallback(() => {
        const el = tabScrollRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el
        setCanScrollLeft(scrollLeft > 4)
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
    }, [])

    useEffect(() => {
        const timer = setTimeout(checkTabScroll, 100)
        const el = tabScrollRef.current
        if (!el) return () => clearTimeout(timer)

        el.addEventListener('scroll', checkTabScroll, { passive: true })
        window.addEventListener('resize', checkTabScroll)
        return () => {
            clearTimeout(timer)
            el.removeEventListener('scroll', checkTabScroll)
            window.removeEventListener('resize', checkTabScroll)
        }
    }, [checkTabScroll, activeTypeCategories])

    const scrollTabs = (direction) => {
        const el = tabScrollRef.current
        if (!el) return
        const scrollAmount = direction === 'left' ? -280 : 280
        el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        setTimeout(checkTabScroll, 350)
    }

    const handleSelectTypeTab = (e, typeId) => {
        setSelectedTypeId(typeId)
        if (e?.currentTarget) {
            e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        }
    }

    // ควบคุมการคลิกเลื่อนแถบ Quick Switcher หมวดหมู่ในหน้ารายการหนังสือ
    const switcherScrollRef = useRef(null)
    const activeChipRef = useRef(null)
    const [canSwitcherScrollLeft, setCanSwitcherScrollLeft] = useState(false)
    const [canSwitcherScrollRight, setCanSwitcherScrollRight] = useState(false)

    const checkSwitcherScroll = useCallback(() => {
        const el = switcherScrollRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el
        setCanSwitcherScrollLeft(scrollLeft > 4)
        setCanSwitcherScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
    }, [])

    useEffect(() => {
        const timer = setTimeout(checkSwitcherScroll, 100)
        const el = switcherScrollRef.current
        if (!el) return () => clearTimeout(timer)

        el.addEventListener('scroll', checkSwitcherScroll, { passive: true })
        window.addEventListener('resize', checkSwitcherScroll)
        return () => {
            clearTimeout(timer)
            el.removeEventListener('scroll', checkSwitcherScroll)
            window.removeEventListener('resize', checkSwitcherScroll)
        }
    }, [checkSwitcherScroll, activeCategories, selectedId])

    // เลื่อนชิปหมวดหมู่ที่เลือกให้อยู่ในมุมมองอัตโนมัติ (เลื่อนเฉพาะใน container แนวนอน ไม่เลื่อนหน้าจอหลัก)
    useEffect(() => {
        if (activeChipRef.current && switcherScrollRef.current) {
            const container = switcherScrollRef.current
            const chip = activeChipRef.current
            const targetLeft = chip.offsetLeft - (container.clientWidth / 2) + (chip.clientWidth / 2)
            container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' })
            setTimeout(checkSwitcherScroll, 350)
        }
    }, [selectedId, checkSwitcherScroll])

    const scrollSwitcher = (direction) => {
        const el = switcherScrollRef.current
        if (!el) return
        const scrollAmount = direction === 'left' ? -280 : 280
        el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        setTimeout(checkSwitcherScroll, 350)
    }

    // ซิงก์ค่า selectedId เมื่อ prop cateId เปลี่ยนแปลง (เช่น ผู้ใช้กด Back / Forward ในเบราว์เซอร์)
    useEffect(() => {
        setSelectedId(cateId || null)
    }, [cateId])

    // ฟังก์ชันดึงข้อมูลหน้าหมวดหมู่ (ทั้งกรณีมี ID และไม่มี ID)
    const fetchCategoryData = useCallback(async (targetId, targetPage = 1) => {
        if (!API) return
        const requestId = ++activeRequestId.current

        try {
            setLoading(true)
            setErrorMsg(null)

            const queryParam = targetId ? `/${encodeURIComponent(targetId)}?page=${targetPage}&limit=24` : `?page=1&limit=24`
            const res = await AxiosGetData(API, `/api/cate_data${queryParam}`, headers, privateKey)

            if (requestId !== activeRequestId.current) return

            if (res) {
                if (Array.isArray(res.categories) && res.categories.length > 0) {
                    setCategories(prev => prev.length === 0 ? res.categories : prev)
                }
                if (Array.isArray(res.type_categories)) {
                    setTypeCategories(prev => prev.length === 0 ? res.type_categories : prev)
                }

                if (res.is_all) {
                    setCurrentCategory(null)
                    setBooks([])
                    setTotalBooks(0)
                } else {
                    setCurrentCategory(res.category || null)
                    const newBooks = Array.isArray(res.books) ? res.books : []
                    setBooks(newBooks)
                    setTotalBooks(res.total ?? 0)
                    setPage(targetPage)
                }
            } else {
                setErrorMsg("ไม่สามารถโหลดข้อมูลหมวดหมู่ได้")
            }
        } catch (err) {
            console.error("Error fetching category data:", err)
            if (requestId === activeRequestId.current) {
                setErrorMsg("เกิดข้อผิดพลาดในการโหลดข้อมูล")
            }
        } finally {
            if (requestId === activeRequestId.current) {
                setLoading(false)
            }
        }
    }, [API, headers, privateKey])

    // เรียกดึงข้อมูลเมื่อ selectedId เปลี่ยน
    useEffect(() => {
        setBooks([])
        setPage(1)
        fetchCategoryData(selectedId, 1)
    }, [selectedId, fetchCategoryData])

    const handlePageChange = (newPage) => {
        setPage(newPage)
        fetchCategoryData(selectedId, newPage)
        if (bookGridRef.current) {
            bookGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleSelectCategory = (catId) => {
        setSelectedId(catId)
        setPage(1)
        router.push(`/cate/${catId}`)
    }

    const handleBackToAllCategories = () => {
        setSelectedId(null)
        setPage(1)
        router.push('/cate')
    }

    return (
        <div className="max-w-[1240px] mx-auto px-3 sm:px-4 md:px-8 py-6 md:py-10">
            {/* ══════════════════════════════════════════════════════════════
                โหมด 1: หน้ารายการหนังสือในหมวดหมู่ (เมื่อมี selectedId)
               ══════════════════════════════════════════════════════════════ */}
            {selectedId ? (
                <>
                    {/* Header Bar & Navigation */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <button
                            onClick={handleBackToAllCategories}
                            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-[8px] border hover:border-[#EC008C] hover:text-[#EC008C] text-primary transition-all text-xs md:text-sm font-medium cursor-pointer shadow-2xs ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            <span>ดูหมวดหมู่ทั้งหมด</span>
                        </button>

                        <Link
                            href="/"
                            className="text-xs md:text-sm text-gray-500 hover:text-primary transition-colors flex items-center gap-1.5"
                        >
                            <FontAwesomeIcon icon={faBookOpen} />
                            <span>กลับสู่หน้าหลัก</span>
                        </Link>
                    </div>

                    {/* Quick Switcher: แถบชิปหมวดหมู่โทนคลีน พร้อมปุ่มเลื่อน */}
                    {activeCategories.length > 0 && (
                        <div className="relative mb-6">
                            {/* ปุ่มเลื่อนซ้าย */}
                            {canSwitcherScrollLeft && (
                                <div className={`absolute left-0 top-0 bottom-2 z-10 flex items-center pr-4 bg-gradient-to-r ${isDark ? 'from-[#0a0a0a]' : 'from-white'} to-transparent pointer-events-none`}>
                                    <button
                                        type="button"
                                        onClick={() => scrollSwitcher('left')}
                                        className={`w-7 h-7 rounded-full border shadow-md flex items-center justify-center cursor-pointer pointer-events-auto transition-all hover:scale-105 active:scale-95 ${isDark ? 'bg-[#1e1e1e] border-zinc-700 text-white hover:bg-zinc-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        title="เลื่อนซ้าย"
                                        aria-label="เลื่อนซ้าย"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                                    </button>
                                </div>
                            )}

                            {/* แถบชิปเลื่อน */}
                            <div
                                ref={switcherScrollRef}
                                onWheel={(e) => {
                                    if (e.deltaY !== 0) {
                                        e.currentTarget.scrollLeft += e.deltaY
                                    }
                                }}
                                className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] scroll-smooth"
                            >
                                <button
                                    onClick={handleBackToAllCategories}
                                    className={`px-3.5 py-1.5 rounded-[8px] text-xs md:text-sm font-medium transition-all cursor-pointer text-primary border whitespace-nowrap hover:border-[#EC008C] hover:text-[#EC008C] shrink-0 ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} className="mr-1.5" />
                                    ทุกหมวดหมู่
                                </button>
                                {activeCategories.map((cat, idx) => {
                                    const isCurrent = String(cat.id) === String(selectedId) || cat.name === selectedId
                                    return (
                                        <button
                                            key={cat.id || idx}
                                            ref={isCurrent ? activeChipRef : null}
                                            onClick={(e) => {
                                                e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
                                                handleSelectCategory(cat.id)
                                            }}
                                            className={`px-3.5 py-1.5 rounded-[8px] text-xs md:text-sm font-medium transition-all cursor-pointer flex items-center gap-1.5 border shrink-0 whitespace-nowrap ${isCurrent
                                                ? 'bg-[#EC008C] text-white border-[#EC008C] shadow-xs'
                                                : `${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'} text-primary hover:border-[#EC008C] hover:text-[#EC008C]`
                                                }`}
                                        >
                                            <span>{cat.name}</span>
                                        </button>
                                    )
                                })}
                            </div>

                            {/* ปุ่มเลื่อนขวา */}
                            {canSwitcherScrollRight && (
                                <div className={`absolute right-0 top-0 bottom-2 z-10 flex items-center pl-4 bg-gradient-to-l ${isDark ? 'from-[#0a0a0a]' : 'from-white'} to-transparent pointer-events-none`}>
                                    <button
                                        type="button"
                                        onClick={() => scrollSwitcher('right')}
                                        className={`w-7 h-7 rounded-full border shadow-md flex items-center justify-center cursor-pointer pointer-events-auto transition-all hover:scale-105 active:scale-95 ${isDark ? 'bg-[#1e1e1e] border-zinc-700 text-white hover:bg-zinc-800' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        title="เลื่อนขวา"
                                        aria-label="เลื่อนขวา"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* หมวดหมู่ Title Banner โทนคลีน สบายตา */}
                    <div ref={bookGridRef} className={`border rounded-[12px] p-5 md:p-6 mb-8 shadow-2xs ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3.5">
                                <img
                                    src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png"
                                    alt="Eve Mascot"
                                    className="w-12 h-12 md:w-14 md:h-14 object-contain shrink-0"
                                />
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-xs uppercase tracking-wider font-semibold text-[#EC008C] px-2 py-0.5 rounded-md border ${isDark ? 'bg-pink-950/50 border-pink-900/50' : 'bg-pink-50 border-pink-100'}`}>
                                            หมวดหมู่
                                        </span>
                                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-primary m-0">
                                            {currentCategory?.name || (isNaN(Number(selectedId)) ? decodeURIComponent(selectedId) : "หมวดหมู่หนังสือ")}
                                        </h1>
                                    </div>
                                    <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        เรียงตามวันวางจำหน่ายล่าสุด
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleBackToAllCategories}
                                className="self-start sm:self-center text-xs md:text-sm text-[#EC008C] hover:text-[#ce326b] font-medium transition-colors inline-flex items-center gap-1 cursor-pointer"
                            >
                                <span>เปลี่ยนหมวดหมู่</span>
                                <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
                            </button>
                        </div>
                    </div>

                    {/* Book Grid Content */}
                    {loading ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 sm:gap-3.5 md:gap-4 lg:gap-6 mb-12">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className={`animate-pulse rounded-[8px] overflow-hidden aspect-[2/3] ${isDark ? 'bg-zinc-800' : 'bg-gray-100'}`}>
                                    <div className={`w-full h-3/4 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'}`}></div>
                                    <div className="p-2 space-y-2">
                                        <div className={`h-3 rounded w-3/4 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'}`}></div>
                                        <div className={`h-2 rounded w-1/2 ${isDark ? 'bg-zinc-700' : 'bg-gray-200'}`}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : books.length > 0 ? (
                        <>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 sm:gap-3.5 md:gap-4 lg:gap-6 mb-8">
                                {books.map((book, index) => (
                                    <CardEbook
                                        key={book.id ? `${book.id}-${index}` : index}
                                        Ebookdata={book}
                                        type="SearchBook"
                                    />
                                ))}
                            </div>

                            {/* Pagination แถบเลือกหน้า */}
                            {totalBooks > 24 && (
                                <div className="flex justify-center items-center mt-6 mb-12 overflow-x-auto [-webkit-overflow-scrolling:touch] py-2 w-full">
                                    <Pagination
                                        current={page}
                                        pageSize={24}
                                        total={totalBooks}
                                        showSizeChanger={false}
                                        onChange={handlePageChange}
                                        responsive={true}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        /* Empty State: ไม่พบหนังสือในหมวดหมู่นี้ */
                        <div className={`text-center py-16 px-4 rounded-[12px] mb-12 border ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}>
                            <img
                                src="https://eve-image.eveebook.com/image.eveebook/mascot/no1.png"
                                alt="Mascot Cute"
                                className="w-20 md:w-24 mx-auto mb-4 object-contain opacity-80"
                            />
                            <h3 className="text-lg md:text-xl font-bold text-primary mb-1">
                                ยังไม่มีรายการหนังสือในหมวดหมู่นี้
                            </h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                                หนังสือในหมวดหมู่นี้อาจอยู่ระหว่างการจัดเตรียม หรือคุณสามารถเลือกดูหมวดหมู่อื่นๆ ที่น่าสนใจได้เลย
                            </p>
                            <button
                                onClick={handleBackToAllCategories}
                                className="px-5 py-2.5 bg-[#EC008C] hover:bg-[#ce326b] text-white rounded-[8px] text-sm font-medium transition-all shadow-sm cursor-pointer"
                            >
                                สำรวจหมวดหมู่อื่นๆ
                            </button>
                        </div>
                    )}
                </>
            ) : (
                /* ══════════════════════════════════════════════════════════════
                    โหมด 2: หน้าแสดงหมวดหมู่ทั้งหมด (แบ่งตามประเภทเหมือนหน้าค้นหา)
                   ══════════════════════════════════════════════════════════════ */
                <>
                    {/* Header Banner โทนคลีน สบายตา */}
                    <div className="relative overflow-hidden rounded-[14px] page-surface  border border-[#d4d4d441] text-primary p-6 sm:p-8 md:p-10 mb-8 shadow">
                        <div className="relative z-10 max-w-2xl">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-[6px]  text-[#EC008C] text-xs font-semibold uppercase tracking-wider mb-3">
                                <FontAwesomeIcon icon={faLayerGroup} />
                                <span>สำรวจหมวดหมู่หนังสือ</span>
                            </span>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary tracking-tight mb-2">
                                หมวดหมู่หนังสือทั้งหมด
                            </h1>
                            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                ค้นพบเรื่องราวที่คุณหลงใหล ทั้งนิยาย การ์ตูน และวรรณกรรมหลากหลายแนว แบ่งตามประเภทหนังสืออย่างเป็นระเบียบ
                            </p>

                            {/* Search Filter Box สำหรับหมวดหมู่ */}
                            <div className="relative max-w-md">
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                                />
                                <input
                                    type="text"
                                    value={categorySearch}
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                    placeholder="พิมพ์ค้นหาชื่อหมวดหมู่... เช่น รัก, ผจญภัย, แฟนตาซี"
                                    className={`w-full pl-11 pr-10 py-2.5 rounded-[8px] border text-primary placeholder-gray-400 text-sm focus:outline-none focus:border-[#EC008C] transition-all ${isDark ? 'bg-zinc-800 border-zinc-700 focus:bg-[#1e1e1e]' : 'bg-gray-50 border-gray-200 focus:bg-white'}`}
                                />
                                {categorySearch && (
                                    <button
                                        onClick={() => setCategorySearch("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm p-1 cursor-pointer"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Mascot Illustration Background */}
                        <img
                            src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png"
                            alt="Eve Rabbit"
                            className="hidden sm:block absolute -right-4 -bottom-4 w-36 md:w-44 lg:w-52 object-contain pointer-events-none drop-shadow-xs select-none opacity-90"
                        />
                    </div>

                    {/* แถบเลือกประเภทหนังสือ (Book Type Filter Tabs พร้อมปุ่มคลิกเลื่อน) */}
                    {activeTypeCategories.length > 0 && !categorySearch && (
                        <div className="relative mb-8">
                            {/* ปุ่มคลิกเลื่อนซ้าย */}
                            {canScrollLeft && (
                                <div className={`absolute left-0 top-0 bottom-2 z-10 flex items-center pr-6 bg-gradient-to-r ${isDark ? 'from-[#0a0a0a]' : 'from-white'} to-transparent pointer-events-none`}>
                                    <button
                                        type="button"
                                        onClick={() => scrollTabs('left')}
                                        className={`w-8 h-8 rounded-full border shadow-md flex items-center justify-center cursor-pointer pointer-events-auto transition-all hover:scale-105 active:scale-95 ${isDark
                                            ? 'bg-[#1e1e1e] border-zinc-700 text-white hover:bg-zinc-800'
                                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        title="เลื่อนซ้าย"
                                        aria-label="เลื่อนซ้าย"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                                    </button>
                                </div>
                            )}

                            {/* แถบแท็บเลื่อน */}
                            <div
                                ref={tabScrollRef}
                                onWheel={(e) => {
                                    if (e.deltaY !== 0) {
                                        e.currentTarget.scrollLeft += e.deltaY
                                    }
                                }}
                                className="flex items-center gap-2.5 overflow-x-auto pb-2 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] scroll-smooth"
                            >
                                {(() => {
                                    const allTheme = getTypeTheme('all', isDark)
                                    const isSelected = selectedTypeId === 'all'
                                    return (
                                        <button
                                            onClick={(e) => handleSelectTypeTab(e, 'all')}
                                            className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 border hover:-translate-y-0.5 active:translate-y-0 ${isSelected ? allTheme.active : allTheme.inactive
                                                }`}
                                        >
                                            <FontAwesomeIcon icon={allTheme.icon} className={`text-xs ${isSelected ? 'text-white' : allTheme.iconColor}`} />
                                            <span>ทั้งหมด</span>
                                            <span
                                                className={`text-[11px] px-2 py-0.5 rounded-full font-bold transition-colors ${isSelected ? allTheme.badgeActive : allTheme.badgeInactive
                                                    }`}
                                            >
                                                {activeCategories.length}
                                            </span>
                                        </button>
                                    )
                                })()}
                                {activeTypeCategories.map((typeGroup, index) => {
                                    const isSelected = String(selectedTypeId) === String(typeGroup.type_id)
                                    const theme = getTypeTheme(typeGroup.type_name, isDark, index)
                                    return (
                                        <button
                                            key={typeGroup.type_id}
                                            onClick={(e) => handleSelectTypeTab(e, typeGroup.type_id)}
                                            className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 border hover:-translate-y-0.5 active:translate-y-0 ${isSelected ? theme.active : theme.inactive
                                                }`}
                                        >
                                            <FontAwesomeIcon icon={theme.icon} className={`text-xs ${isSelected ? 'text-white' : theme.iconColor}`} />
                                            <span>{typeGroup.type_name}</span>
                                            <span
                                                className={`text-[11px] px-2 py-0.5 rounded-full font-bold transition-colors ${isSelected ? theme.badgeActive : theme.badgeInactive
                                                    }`}
                                            >
                                                {typeGroup.categories?.length ?? 0}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            {/* ปุ่มคลิกเลื่อนขวา */}
                            {canScrollRight && (
                                <div className={`absolute right-0 top-0 bottom-2 z-10 flex items-center pl-6 bg-gradient-to-l ${isDark ? 'from-[#0a0a0a]' : 'from-white'} to-transparent pointer-events-none`}>
                                    <button
                                        type="button"
                                        onClick={() => scrollTabs('right')}
                                        className={`w-8 h-8 rounded-full border shadow-md flex items-center justify-center cursor-pointer pointer-events-auto transition-all hover:scale-105 active:scale-95 ${isDark
                                            ? 'bg-[#1e1e1e] border-zinc-700 text-white hover:bg-zinc-800'
                                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        title="เลื่อนขวา"
                                        aria-label="เลื่อนขวา"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* การแสดงผลแบ่งหมวดหมู่ตามประเภท (Grouped by Type Sections) */}
                    {loading && categories.length === 0 ? (
                        <div className="space-y-8 mb-12">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="space-y-3">
                                    <div className={`h-6 rounded w-36 animate-pulse ${isDark ? 'bg-zinc-800' : 'bg-gray-200'}`}></div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 md:gap-5">
                                        {Array.from({ length: 5 }).map((_, j) => (
                                            <div key={j} className={`animate-pulse rounded-[8px] p-5 h-28 ${isDark ? 'bg-zinc-800' : 'bg-gray-100'}`}></div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : displayedTypeGroups.length > 0 ? (
                        <div className="space-y-10 mb-16">
                            {displayedTypeGroups.map((typeGroup, index) => {
                                const theme = getTypeTheme(typeGroup.type_name, isDark, index)
                                return (
                                    <div key={typeGroup.type_id} className="space-y-4">
                                        {/* หัวข้อประเภทหนังสือ (เหมือนหน้าค้นหา) */}
                                        <div className={`flex items-center justify-between pb-2 border-b ${isDark ? 'border-zinc-800' : 'border-gray-200'}`}>
                                            <div className="flex items-center gap-2.5">
                                                <span className={`w-2 h-5 rounded-full ${theme.bar}`}></span>
                                                <h2 className="text-lg md:text-xl font-bold text-primary m-0 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={theme.icon} className={`text-sm ${theme.iconColor}`} />
                                                    <span>{typeGroup.type_name}</span>
                                                    <span className="text-xs font-normal text-gray-400">
                                                        ({typeGroup.categories?.length} หมวดหมู่)
                                                    </span>
                                                </h2>
                                            </div>
                                        </div>

                                        {/* Grid การ์ดหมวดหมู่ในประเภทนี้ */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 md:gap-5">
                                            {typeGroup.categories.map((cat, index) => (
                                                <div
                                                    key={cat.id || index}
                                                    onClick={() => handleSelectCategory(cat.id)}
                                                    className={`group border rounded-[10px] p-4 md:p-5 cursor-pointer shadow-2xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 flex items-center justify-between min-h-[60px] hover:border-[#EC008C] ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}
                                                >
                                                    <h3 className="text-sm sm:text-base font-semibold text-primary group-hover:text-[#EC008C] transition-colors line-clamp-2 m-0">
                                                        {cat.name}
                                                    </h3>
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className={`text-xs group-hover:text-[#EC008C] group-hover:translate-x-0.5 transition-all shrink-0 ml-2.5 ${isDark ? 'text-zinc-600' : 'text-gray-300'}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : filteredCategories.length > 0 ? (
                        /* Fallback กรณีไม่มี type_categories แต่มี categories ทั่วไป */
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 md:gap-5 mb-16">
                            {filteredCategories.map((cat, index) => (
                                <div
                                    key={cat.id || index}
                                    onClick={() => handleSelectCategory(cat.id)}
                                    className={`group border rounded-[10px] p-4 md:p-5 cursor-pointer shadow-2xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 flex items-center justify-between min-h-[60px] hover:border-[#EC008C] ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}
                                >
                                    <h3 className="text-sm sm:text-base font-semibold text-primary group-hover:text-[#EC008C] transition-colors line-clamp-2 m-0">
                                        {cat.name}
                                    </h3>
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className={`text-xs group-hover:text-[#EC008C] group-hover:translate-x-0.5 transition-all shrink-0 ml-2.5 ${isDark ? 'text-zinc-600' : 'text-gray-300'}`}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Empty Search Result */
                        <div className={`text-center py-16 rounded-[12px] border mb-12 ${isDark ? 'bg-[#1e1e1e] border-zinc-800' : 'bg-white border-gray-200'}`}>
                            <p className="text-gray-500 mb-3">
                                ไม่พบหมวดหมู่ที่ตรงกับคำค้นหา &ldquo;<span className="font-semibold text-primary">{categorySearch}</span>&rdquo;
                            </p>
                            <button
                                onClick={() => setCategorySearch("")}
                                className="text-xs text-[#EC008C] hover:underline cursor-pointer"
                            >
                                ล้างคำค้นหา
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default Cate
